/**
 * Device Type Mapper - Map backend device types to mobile enum
 *
 * Problem: Backend sends device types in various formats (snake_case, lowercase, etc.)
 * Solution: Normalize all variants to match mobile DeviceType enum
 *
 * Created: Feb 4, 2026
 * Part of: MOBILE-UX-001 fix
 */

import { DeviceType } from '../constants/deviceTypes';

/**
 * Map backend device type strings to mobile DeviceType enum
 *
 * @param backendType - Device type string from backend API
 * @returns Normalized DeviceType enum value
 *
 * @example
 * mapBackendDeviceType('temp_humidity_sensor') // Returns DeviceType.TEMP_HUMIDITY
 * mapBackendDeviceType('TEMPERATURE') // Returns DeviceType.TEMPERATURE
 * mapBackendDeviceType('water_tank') // Returns DeviceType.WATER
 */
export function mapBackendDeviceType(backendType: string | undefined | null): DeviceType {
  // Handle null/undefined
  if (!backendType) {
    return DeviceType.SENSOR;
  }

  // Normalize: lowercase and trim
  const normalized = backendType.toLowerCase().trim();

  // Mapping table: backend format → mobile DeviceType
  const mapping: Record<string, DeviceType> = {
    // Generic sensor
    sensor: DeviceType.SENSOR,
    generic: DeviceType.SENSOR,
    generic_sensor: DeviceType.SENSOR,

    // Temperature sensors
    temperature: DeviceType.TEMPERATURE,
    temp: DeviceType.TEMPERATURE,
    temp_sensor: DeviceType.TEMPERATURE,
    temperature_sensor: DeviceType.TEMPERATURE,

    // Humidity sensors
    humidity: DeviceType.HUMIDITY,
    humid: DeviceType.HUMIDITY,
    humidity_sensor: DeviceType.HUMIDITY,

    // Combined temp+humidity
    temp_humidity: DeviceType.TEMP_HUMIDITY,
    temp_humidity_sensor: DeviceType.TEMP_HUMIDITY,
    temperature_humidity: DeviceType.TEMP_HUMIDITY,
    temperature_humidity_sensor: DeviceType.TEMP_HUMIDITY,
    th_sensor: DeviceType.TEMP_HUMIDITY,

    // Tank/Level sensors
    level: DeviceType.LEVEL,
    level_sensor: DeviceType.LEVEL,
    tank: DeviceType.LEVEL,
    tank_sensor: DeviceType.LEVEL,
    tank_level: DeviceType.LEVEL,

    // Water sensors
    water: DeviceType.WATER,
    water_tank: DeviceType.WATER,
    water_level: DeviceType.WATER,
    water_sensor: DeviceType.WATER,

    // Diesel sensors
    diesel: DeviceType.DIESEL,
    diesel_tank: DeviceType.DIESEL,
    diesel_level: DeviceType.DIESEL,
    fuel: DeviceType.DIESEL,
    fuel_tank: DeviceType.DIESEL,

    // Gas sensors
    gas: DeviceType.GAS,
    gas_sensor: DeviceType.GAS,
    gas_detector: DeviceType.GAS,
    lpg: DeviceType.GAS,

    // Energy/Power sensors
    energy: DeviceType.ENERGY,
    energy_meter: DeviceType.ENERGY,
    energy_monitor: DeviceType.ENERGY,
    power: DeviceType.POWER,
    power_meter: DeviceType.POWER,
    electricity: DeviceType.POWER,

    // Motion sensors
    motion: DeviceType.MOTION,
    motion_sensor: DeviceType.MOTION,
    pir: DeviceType.MOTION,
    movement: DeviceType.MOTION,

    // Door sensors
    door: DeviceType.DOOR,
    door_sensor: DeviceType.DOOR,
    contact: DeviceType.DOOR,
    contact_sensor: DeviceType.DOOR,
    magnetic: DeviceType.DOOR,

    // Camera
    camera: DeviceType.CAMERA,
    cam: DeviceType.CAMERA,
    video: DeviceType.CAMERA,
    cctv: DeviceType.CAMERA,

    // Actuator
    actuator: DeviceType.ACTUATOR,
    switch: DeviceType.ACTUATOR,
    relay: DeviceType.ACTUATOR,
    controller: DeviceType.ACTUATOR,

    // Multi-sensor
    multi_sensor: DeviceType.MULTI_SENSOR,
    multisensor: DeviceType.MULTI_SENSOR,
    multi: DeviceType.MULTI_SENSOR,
    combo: DeviceType.MULTI_SENSOR,
  };

  // Lookup in mapping table
  const mappedType = mapping[normalized];

  if (mappedType) {
    return mappedType;
  }

  // Fallback: Try to match partial keywords
  if (normalized.includes('temp') && normalized.includes('humi')) {
    return DeviceType.TEMP_HUMIDITY;
  }
  if (normalized.includes('temp')) {
    return DeviceType.TEMPERATURE;
  }
  if (normalized.includes('humi')) {
    return DeviceType.HUMIDITY;
  }
  if (normalized.includes('water')) {
    return DeviceType.WATER;
  }
  if (normalized.includes('diesel') || normalized.includes('fuel')) {
    return DeviceType.DIESEL;
  }
  if (normalized.includes('gas')) {
    return DeviceType.GAS;
  }
  if (normalized.includes('level') || normalized.includes('tank')) {
    return DeviceType.LEVEL;
  }
  if (normalized.includes('motion') || normalized.includes('pir')) {
    return DeviceType.MOTION;
  }
  if (normalized.includes('door') || normalized.includes('contact')) {
    return DeviceType.DOOR;
  }
  if (normalized.includes('camera') || normalized.includes('video')) {
    return DeviceType.CAMERA;
  }
  if (
    normalized.includes('actuator') ||
    normalized.includes('switch') ||
    normalized.includes('relay')
  ) {
    return DeviceType.ACTUATOR;
  }
  if (normalized.includes('energy') || normalized.includes('power')) {
    return DeviceType.ENERGY;
  }

  // Ultimate fallback: generic sensor
  return DeviceType.SENSOR;
}

/**
 * Get device status information with clear reason
 *
 * @param deviceInfo - Device information object
 * @param isArabic - Whether to use Arabic translations
 * @returns Status info with color, icon, label, and reason
 */
export interface DeviceStatusInfo {
  status: 'active' | 'offline' | 'disabled' | 'inactive';
  color: string;
  icon: string;
  label: string;
  reason: string;
}

export function getDeviceStatusInfo(
  deviceInfo: {
    status?: string;
    lastSeenAt?: string;
  },
  isArabic: boolean = false,
): DeviceStatusInfo {
  const lastSeen = deviceInfo.lastSeenAt ? new Date(deviceInfo.lastSeenAt) : null;
  const now = new Date();
  const minutesSinceLastSeen = lastSeen
    ? Math.floor((now.getTime() - lastSeen.getTime()) / 60000)
    : Infinity;

  // Case 1: Device is disabled manually
  if (deviceInfo.status === 'disabled') {
    return {
      status: 'disabled',
      color: '#6b7280', // Gray
      icon: 'ban',
      label: isArabic ? 'معطّل' : 'Disabled',
      reason: isArabic ? 'تم تعطيل الجهاز يدوياً' : 'Device manually disabled',
    };
  }

  // Case 2: Device is offline (no data for >15 minutes)
  if (minutesSinceLastSeen > 15) {
    return {
      status: 'offline',
      color: '#ef4444', // Red
      icon: 'cloud-offline',
      label: isArabic ? 'غير متصل' : 'Offline',
      reason: isArabic
        ? `لم يرسل بيانات منذ ${minutesSinceLastSeen} دقيقة`
        : `No data for ${minutesSinceLastSeen} min`,
    };
  }

  // Case 3: Device is inactive (status field says so, but recently seen)
  if (deviceInfo.status === 'inactive') {
    return {
      status: 'inactive',
      color: '#f59e0b', // Orange
      icon: 'alert-circle',
      label: isArabic ? 'غير نشط' : 'Inactive',
      reason: isArabic ? 'معطّل مؤقتاً' : 'Temporarily disabled',
    };
  }

  // Case 4: Device is active
  return {
    status: 'active',
    color: '#10b981', // Green
    icon: 'checkmark-circle',
    label: isArabic ? 'نشط' : 'Active',
    reason: isArabic ? 'يعمل بشكل طبيعي' : 'Working normally',
  };
}

/**
 * Format relative time in user-friendly way
 *
 * @param dateString - ISO date string
 * @param isArabic - Whether to use Arabic
 * @returns Formatted relative time (e.g., "5 min ago", "2h ago")
 */
export function formatRelativeTime(
  dateString: string | undefined | null,
  isArabic: boolean = false,
): string {
  if (!dateString) {
    return isArabic ? 'غير معروف' : 'Unknown';
  }

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  // Just now (< 1 min)
  if (diffMins < 1) {
    return isArabic ? 'الآن' : 'Just now';
  }

  // Minutes (< 60 min)
  if (diffMins < 60) {
    return isArabic
      ? `منذ ${diffMins} ${diffMins === 1 ? 'دقيقة' : 'دقائق'}`
      : `${diffMins} min ago`;
  }

  // Hours (< 24h)
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return isArabic
      ? `منذ ${diffHours} ${diffHours === 1 ? 'ساعة' : 'ساعات'}`
      : `${diffHours}h ago`;
  }

  // Days (< 7 days)
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return isArabic ? `منذ ${diffDays} ${diffDays === 1 ? 'يوم' : 'أيام'}` : `${diffDays}d ago`;
  }

  // Absolute date for older
  return date.toLocaleString(isArabic ? 'ar-SA' : 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
